import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Toaster } from '../components/ui/toaster';
import { projects } from '../mock';

// ---- constants (no more magic numbers sprinkled in the JSX) ----
const REVEAL_FALLBACK_MS = 900;
const REVEAL_OBSERVER_OPTS = {
  threshold: 0.08,
  rootMargin: '0px 0px -5% 0px',
};
const DEFAULT_VIDEO_ASPECT = '886 / 1920';
const DEFAULT_REEL_ASPECT = '9 / 16';

// Non-nested layout selector for the classic image gallery (replaces nested ternary)
const getImageLayout = (index) => {
  const mod = index % 4;
  if (mod === 0) return 'md:col-span-12 aspect-[16/9]';
  if (mod === 1) return 'md:col-span-7 aspect-[4/3]';
  if (mod === 2) return 'md:col-span-5 aspect-[3/4]';
  return 'md:col-span-12 aspect-[21/9]';
};

// Stable key for a gallery item (image URL or video src)
const galleryItemKey = (item, index) => {
  if (typeof item === 'string') return item;
  if (item && typeof item === 'object') return item.src || `item-${index}`;
  return `item-${index}`;
};

const ProjectPage = ({ project }) => {
  const revealRefs = useRef([]);
  revealRefs.current = [];
  const addRef = (el) => {
    if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el);
  };

  // Mount/route-change only — intentionally only depends on slug.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, REVEAL_OBSERVER_OPTS);
    revealRefs.current.forEach((el) => io.observe(el));
    const fallbackTimer = setTimeout(() => {
      revealRefs.current.forEach((el) => el && el.classList.add('is-visible'));
    }, REVEAL_FALLBACK_MS);
    return () => {
      io.disconnect();
      clearTimeout(fallbackTimer);
    };
  }, [project.slug]);

  const idx = projects.findIndex((p) => p.slug === project.slug);
  const next = projects[(idx + 1) % projects.length];

  // Memoised reel list — avoids re-filtering on every render
  const reelOnlyItems = useMemo(
    () =>
      project.gallery.filter(
        (it) => typeof it === 'object' && it !== null && it.type === 'video'
      ),
    [project.gallery]
  );

  return (
    <div className="bg-cream text-ink">
      <Header transparent />

      {/* Hero */}
      <section className="relative h-[88svh] min-h-[560px] w-full overflow-hidden">
        <img
          src={project.cover}
          alt={project.title}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.68) saturate(0.98)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/10 to-black/55" />

        <div className="relative z-10 h-full max-w-[1480px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-16 lg:pb-24">
          <Link
            to="/#projects"
            className="inline-flex items-center gap-2 text-cream-soft/90 hover:text-gold transition-colors text-[11px] tracking-[0.28em] uppercase font-semibold mb-6"
          >
            <ArrowLeft size={16} strokeWidth={1.5} /> Back to projects
          </Link>

          <div className="flex items-center gap-3 text-cream-soft/80 text-[11px] tracking-[0.28em] uppercase mb-3">
            {project.year && (
              <>
                <span>{project.year}</span>
                <span className="h-px w-10 bg-cream-soft/60" />
              </>
            )}
            <span>{project.subtitle}</span>
          </div>
          <h1 className="font-display text-cream-soft text-[clamp(2.6rem,8vw,6.4rem)] leading-[0.95] text-shadow-deep">
            {project.title}
          </h1>
          {project.role && (
            <p className="mt-5 max-w-xl font-serif italic text-cream-soft/90 text-lg text-shadow-soft">
              {project.role}
            </p>
          )}
        </div>
      </section>

      {/* Description */}
      {!project.hideDetails && (
        <section className="bg-cream-soft py-20 lg:py-28">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div ref={addRef} className="reveal lg:col-span-4">
              <p className="font-script text-olive text-5xl leading-none">about this</p>
              <h2 className="font-display text-olive-deep text-4xl mt-2">Project</h2>
            </div>
            <div ref={addRef} className="reveal lg:col-span-8">
              <p className="font-serif text-ink text-[20px] leading-[1.75]">
                {project.description}
              </p>

              <dl className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-8 border-t border-ink/15 pt-8">
                <div>
                  <dt className="text-[10px] tracking-[0.28em] uppercase text-ink/55 font-semibold">Year</dt>
                  <dd className="font-display text-2xl mt-2">{project.year}</dd>
                </div>
                <div>
                  <dt className="text-[10px] tracking-[0.28em] uppercase text-ink/55 font-semibold">Role</dt>
                  <dd className="font-serif italic text-lg mt-2 leading-snug">{project.role}</dd>
                </div>
                <div>
                  <dt className="text-[10px] tracking-[0.28em] uppercase text-ink/55 font-semibold">Category</dt>
                  <dd className="font-serif italic text-lg mt-2">{project.subtitle}</dd>
                </div>
              </dl>
            </div>
          </div>
        </section>
      )}

      {/* Reel grid (vertical videos, like BTS L'Oréal) */}
      {project.reelGrid && (
        <section className="bg-cream py-16 lg:py-24">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            {project.reelHeading && (
              <div ref={addRef} className="reveal text-center mb-10 lg:mb-14">
                <p className="font-script text-olive text-5xl leading-none">reel</p>
                <h2 className="font-display text-ink text-[clamp(2rem,4vw,3.4rem)] mt-1">
                  {project.reelHeading}
                </h2>
              </div>
            )}
            <div
              className={
                project.reelCols === 3
                  ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 lg:gap-6'
                  : 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6'
              }
            >
              {reelOnlyItems.map((item, i) => (
                <figure
                  key={galleryItemKey(item, i)}
                  ref={addRef}
                  className="reveal overflow-hidden bg-ink/5"
                  style={{ aspectRatio: item.aspect || DEFAULT_REEL_ASPECT }}
                >
                  <video
                    src={item.src}
                    poster={item.poster}
                    className="w-full h-full object-cover block"
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                    preload="metadata"
                  />
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {!project.reelGrid && (
      <section className="bg-cream py-16 lg:py-24">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {project.gallery.map((item, i) => {
            const key = galleryItemKey(item, i);
            const isVideo =
              typeof item === 'object' && item !== null && item.type === 'video';

            if (isVideo) {
              const aspect = item.aspect || DEFAULT_VIDEO_ASPECT;

              // Full-width landscape video (e.g. 16:9 ads)
              if (item.fullWidth) {
                return (
                  <figure
                    key={key}
                    ref={addRef}
                    className="reveal md:col-span-12 bg-ink/5 overflow-hidden"
                    style={{ aspectRatio: aspect }}
                  >
                    <video
                      src={item.src}
                      poster={item.poster}
                      className="w-full h-full object-cover block"
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls
                      preload="metadata"
                    />
                  </figure>
                );
              }

              // Vertical reel — side-by-side with companion image, or half-width if no pair
              const hasPair = Boolean(item.pair);
              const rowClass = hasPair
                ? 'reveal md:col-span-12 grid grid-cols-12 gap-6 lg:gap-8'
                : 'reveal md:col-span-6';
              const videoFigureClass = hasPair
                ? 'col-span-12 md:col-span-5 lg:col-span-4 bg-ink/5 overflow-hidden'
                : 'bg-ink/5 overflow-hidden';

              return (
                <div key={key} ref={addRef} className={rowClass}>
                  <figure
                    className={videoFigureClass}
                    style={{ aspectRatio: aspect }}
                  >
                    <video
                      src={item.src}
                      poster={item.poster}
                      className="w-full h-full object-cover block"
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls
                      preload="metadata"
                    />
                  </figure>
                  {hasPair && (
                    <figure className="hidden md:block md:col-span-7 lg:col-span-8 bg-ink/5 overflow-hidden">
                      <img
                        src={item.pair}
                        alt={`${project.title} — companion`}
                        className="w-full h-full object-cover"
                      />
                    </figure>
                  )}
                </div>
              );
            }

            return (
              <figure
                key={key}
                ref={addRef}
                className={`reveal overflow-hidden bg-ink/5 ${getImageLayout(i)}`}
              >
                <img
                  src={item}
                  alt={`${project.title} — ${i + 1}`}
                  className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-[1200ms] ease-out"
                />
              </figure>
            );
          })}
        </div>
      </section>
      )}

      {/* Next project */}
      <section className="relative bg-olive-deep text-cream-soft overflow-hidden">
        <Link
          to={`/projects/${next.slug}`}
          className="group block"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 items-center gap-10">
            <div className="lg:col-span-7">
              <p className="text-[11px] tracking-[0.28em] uppercase font-semibold text-cream-soft/70">Next project</p>
              <h3 className="font-display text-[clamp(2.6rem,6vw,5rem)] leading-[0.95] mt-3 group-hover:text-gold transition-colors">
                {next.title}
              </h3>
              <p className="font-serif italic text-cream-soft/80 text-xl mt-3">{next.subtitle}</p>
              <div className="inline-flex items-center gap-2 mt-8 text-[11px] tracking-[0.28em] uppercase font-semibold group-hover:text-gold transition-colors">
                View project <ArrowUpRight size={18} strokeWidth={1.5} />
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={next.cover}
                  alt={next.title}
                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1200ms] ease-out"
                />
              </div>
            </div>
          </div>
        </Link>
      </section>

      <Footer />
      <Toaster />

      <style>{`
        .reveal { opacity: 1; transform: translateY(0); transition: opacity .9s ease, transform .9s ease; }
        @media (min-width: 1024px) {
          .reveal { opacity: 0; transform: translateY(28px); }
          .reveal.is-visible { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ProjectPage;
